import type { ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, Loader2 } from 'lucide-react';

type ConfirmTone = 'primary' | 'danger';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string | null;
  cancelLabel?: string;
  tone?: ConfirmTone;
  isLoading?: boolean;
  children?: ReactNode;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Retour',
  tone = 'primary',
  isLoading = false,
  children,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const confirmClasses = tone === 'danger'
    ? 'bg-red-600 text-white hover:bg-red-700 shadow-red-500/20'
    : 'bg-brand-primary text-white hover:bg-brand-accent shadow-brand-primary/20';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] bg-brand-primary/20 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => {
            if (!isLoading) onClose();
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            className="w-full max-w-xl max-h-[90dvh] overflow-y-auto bg-white rounded-[32px] border border-brand-primary/10 shadow-2xl p-6 md:p-8"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start gap-4 mb-5">
              <div className="w-11 h-11 rounded-2xl bg-brand-paper border border-brand-primary/10 flex items-center justify-center shrink-0">
                <AlertTriangle size={18} className={tone === 'danger' ? 'text-red-600' : 'text-brand-accent'} />
              </div>
              <div className="space-y-2">
                <h3 className="text-2xl font-serif font-bold italic text-brand-primary leading-tight">{title}</h3>
                <p className="text-sm md:text-base leading-relaxed text-brand-primary/70">{message}</p>
              </div>
            </div>

            {children ? <div className="mb-5">{children}</div> : null}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-5 py-3 rounded-full border border-brand-primary/10 text-sm font-bold tracking-[0.15em] uppercase text-brand-primary hover:bg-brand-paper transition-all disabled:opacity-60"
              >
                {cancelLabel}
              </button>
              {confirmLabel ? (
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={isLoading}
                  className={`px-5 py-3 rounded-full text-sm font-bold tracking-[0.15em] uppercase transition-all shadow-lg disabled:opacity-60 inline-flex items-center justify-center gap-2 ${confirmClasses}`}
                >
                  {isLoading ? <Loader2 size={16} className="animate-spin" /> : null}
                  {confirmLabel}
                </button>
              ) : null}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
